import { ChevronDown, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { useState } from 'react';
import { useChannels, useNowPlaying } from '../lib/api/channels';
import { useLocaleStore } from '../lib/locales';
import { useRadioPlayerStore } from '../lib/radioPlayerStore';
import { cn } from '../lib/utils';

/**
 * Sidebar broadcast console.
 *
 * Two visual states share the same compact footprint:
 *   - **Idle**: white surface, hairline rules, ink-subtle type. Recedes
 *     into the sidebar so it doesn't fight the nav for attention.
 *   - **On-air**: subtle indigo wash + brand-coloured accents. Stays
 *     within the same light-surface family as the rest of the sidebar
 *     (no jarring dark block), while the live AIR badge + animated
 *     equaliser do the work of saying "this is broadcasting now."
 *
 * Volume + mute live in their familiar slots: the speaker icon toggles
 * mute on click, the thin slider directly below sets level. Native
 * `<input type="range">` so click, drag, and keyboard all work without
 * us re-implementing the studio-monitor wheel.
 */
export function RadioPlayer(): JSX.Element {
  const store = useRadioPlayerStore();
  const locale = useLocaleStore((s) => s.current);
  const channels = useChannels();
  const streamChannels = (channels.data?.items ?? []).filter((c) => !!c.streamUrl);

  // Local selection: which channel the UI is showing (not necessarily playing).
  const [localSlug, setLocalSlug] = useState<string | null>(null);

  const displayChannel = (() => {
    if (store.playing && store.channelSlug) {
      return streamChannels.find((c) => c.slug === store.channelSlug) ?? streamChannels[0];
    }
    return streamChannels.find((c) => c.slug === localSlug) ?? streamChannels[0];
  })();

  const np = useNowPlaying(displayChannel?.slug ?? null, locale);
  const isThisChannelPlaying = store.playing && store.channelSlug === displayChannel?.slug;
  const online = np.data?.stream?.online;
  const noStreams = streamChannels.length === 0;
  const onAir = !!isThisChannelPlaying;

  const handlePlayPause = () => {
    if (!displayChannel?.streamUrl) return;
    if (store.channelSlug === displayChannel.slug) {
      store.toggle();
    } else {
      // Pass the RAW upstream URL — the store wraps it via `streamProxyUrl`
      // when assigning `audio.src`. Persisting the pre-wrapped value froze a
      // baked baseUrl into localStorage during the von.muzi.ng → api.vonnews.ng
      // migration.
      store.setChannel(displayChannel.slug, displayChannel.name, displayChannel.streamUrl);
    }
  };

  const handleChannelChange = (slug: string) => {
    setLocalSlug(slug);
    if (store.playing && store.channelSlug && store.channelSlug !== slug) {
      const ch = streamChannels.find((c) => c.slug === slug);
      if (ch?.streamUrl) {
        store.setChannel(ch.slug, ch.name, ch.streamUrl);
      }
    }
  };

  // Pick a station-identifier line for the now-playing row.
  const trackLine = (() => {
    if (store.error) return { text: store.error, tone: 'error' as const };
    if (store.pendingResume) {
      return {
        text: `Tap to resume${store.channelName ? ` · ${store.channelName}` : ''}`,
        tone: 'resume' as const,
      };
    }
    if (onAir) {
      return {
        text:
          np.data?.stream?.title ??
          np.data?.current?.title ??
          'Live broadcast',
        tone: 'live' as const,
      };
    }
    if (noStreams) return { text: 'No stream configured', tone: 'idle' as const };
    return { text: 'Tap play to tune in', tone: 'idle' as const };
  })();

  return (
    <div
      className={cn(
        'console-on-air relative border-t transition-colors duration-300',
        onAir
          ? 'border-brand/30 bg-gradient-to-br from-brand-soft/70 via-surface to-brand-soft/40'
          : 'border-line bg-surface',
      )}
    >
      {/* Top hairline accent — tricolor when on-air (subtle Nigerian flag
       *  reference), neutral when idle. Doubles as the seam between
       *  SidebarNav and the console. */}
      <div
        className={cn(
          'absolute inset-x-0 top-0 h-px',
          onAir ? 'tricolor-rule opacity-90' : 'bg-line',
        )}
        aria-hidden
      />

      {/* ─── Row 1: AIR badge · channel · controls ─────────────────── */}
      <div className="relative flex items-center gap-2 px-3 pt-3">
        {/* AIR badge. Solid red box with a subtle glow when live; muted
         *  pill when off-air. The 4-bar equaliser inside the badge swaps
         *  state from `idle` (flat 18 % bars) to `live` (asymmetric
         *  bouncing) so the badge "reacts" to broadcast state. */}
        <div
          className={cn(
            'flex h-5 shrink-0 items-center gap-1 rounded-sm px-1.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.18em]',
            onAir
              ? 'bg-live text-white shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_0_12px_rgba(220,38,38,0.55)]'
              : online
                ? 'bg-ink/5 text-ink'
                : 'bg-ink/5 text-ink-subtle',
          )}
        >
          <span
            className={cn(
              'eqwave',
              onAir ? 'text-white' : online ? 'text-ink' : 'text-ink-subtle',
            )}
            data-state={onAir ? 'live' : 'idle'}
            aria-hidden
          >
            <span /><span /><span /><span />
          </span>
          {onAir ? 'On Air' : online ? 'Ready' : 'Off'}
        </div>

        {/* Channel selector. Custom-styled trigger with an invisible
         *  native <select> overlaid for accessibility — keeps the
         *  hardware feel without re-implementing keyboard navigation. */}
        <div className="relative min-w-0 flex-1">
          <div className="pointer-events-none flex min-w-0 items-baseline gap-1 truncate font-heading text-[12.5px] font-semibold tracking-tight text-ink">
            <span className="truncate">
              {displayChannel?.name ?? (noStreams ? 'No stream' : '—')}
            </span>
            {streamChannels.length > 1 ? (
              <ChevronDown className="h-3 w-3 shrink-0 text-ink-subtle opacity-70" />
            ) : null}
          </div>
          {streamChannels.length > 1 ? (
            <select
              value={displayChannel?.slug ?? ''}
              onChange={(e) => handleChannelChange(e.target.value)}
              className="absolute inset-0 cursor-pointer opacity-0"
              aria-label="Select channel"
            >
              {streamChannels.map((ch) => (
                <option key={ch.slug} value={ch.slug}>
                  {ch.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {/* Mute toggle — single-click toggles, no popover gymnastics. */}
        <button
          type="button"
          onClick={store.toggleMute}
          disabled={noStreams}
          className={cn(
            'grid h-6 w-6 shrink-0 place-items-center rounded transition-colors disabled:opacity-30',
            store.muted
              ? 'bg-ink/5 text-ink hover:bg-ink/10'
              : 'text-ink-subtle hover:bg-ink/5 hover:text-ink',
          )}
          title={store.muted ? 'Unmute' : 'Mute'}
          aria-label={store.muted ? 'Unmute' : 'Mute'}
          aria-pressed={store.muted}
        >
          {store.muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>

        {/* Play / Pause — the centrepiece. Brand pill in both states;
         *  shadow grows + colour deepens on-air to give it presence
         *  without needing the panel itself to go dark. */}
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={noStreams || !displayChannel?.streamUrl}
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full text-white transition-all duration-200 disabled:opacity-30',
            onAir
              ? 'bg-brand shadow-[0_0_0_3px_rgba(49,51,128,0.18),0_4px_14px_-4px_rgba(49,51,128,0.5)] hover:bg-brand-hover'
              : 'bg-brand shadow-[0_2px_8px_-2px_rgba(49,51,128,0.45)] hover:bg-brand-hover',
          )}
          aria-label={onAir ? 'Pause' : 'Play'}
          title={onAir ? 'Pause' : 'Play'}
        >
          {onAir ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5 translate-x-px" />
          )}
        </button>
      </div>

      {/* ─── Row 2: now-playing / status line ──────────────────────── */}
      <p
        className={cn(
          'relative mt-1 truncate px-3 text-[10.5px] leading-tight',
          trackLine.tone === 'error' && 'text-danger',
          trackLine.tone === 'resume' && 'font-medium text-brand',
          trackLine.tone === 'live' && 'text-ink-muted',
          trackLine.tone === 'idle' && 'text-ink-subtle',
        )}
        title={trackLine.text}
      >
        {trackLine.tone === 'live' ? (
          <span className="font-mono uppercase tracking-[0.12em] text-ink-subtle/80">
            Now ·{' '}
          </span>
        ) : null}
        {trackLine.text}
      </p>

      {/* ─── Row 3: volume slider ──────────────────────────────────────
       *  Native <input type="range"> for reliable click + drag +
       *  keyboard. Styled minimally (4px track, accent-brand thumb) so
       *  it reads as a hairline meter rather than a desktop-app control.
       *  Generous py spacing because the parent has `overflow: hidden`
       *  (for the diagonal sweep effect) and the native thumb extends
       *  ~6px above and below the 4px track — without padding the thumb
       *  gets clipped at the bottom edge of the console. */}
      {!noStreams ? (
        <div className="relative px-3 pb-4 pt-3">
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={store.muted ? 0 : store.volume}
            onChange={(e) => store.setVolume(Number(e.target.value))}
            className="block h-1 py-2 w-full cursor-pointer accent-brand"
            title={`Volume · ${store.muted ? 'muted' : `${Math.round(store.volume * 100)}%`}`}
            aria-label="Volume"
          />
        </div>
      ) : (
        <div className="pb-3" />
      )}
    </div>
  );
}
